import tinycolor from 'tinycolor2';
import { createObjectWithKey, deepMerge } from './helpers';

export const DEFAULT = 'default';
export const DARK = 'dark';

const base = {
  axisTextColor: '#999999',
  axisLineColor: '#d6d6d6',

  chartColors: [
    '#1f7fbe',
    '#697bc9',
    '#a273c5',
    '#d068b1',
    '#ef6391',
    '#fd6b6a',
    '#f78042',
    '#df9a16',
  ],

  gridLineColor: '#eff0f0',

  legendFontWeight: '400',
  legendTextColor: '#999',
  legendTextColorDisabled: '#717171',
  legendTextColorHover: '#717171',

  loadingBackground: 'rgba(0, 0, 0, 0.4)',
  loadingInverted: true,
  loadingTextColor: '#fff',

  numberTextColor: '#444',
  numberBackgroundColor: '#fff',
  numberInvertTextColor: '#fff',
  numberInvertPositiveBackgroundColor: '#2cc36b',
  numberInvertNegativeBackgroundColor: '#d14233',
  numberPositiveColor: '#2cc36b',
  numberNegativeColor: '#d14233',
  numberNeutralColor: '#999',

  panelBackgroundColor: '#fff',
  panelBoxShadow: '0px 2px 2px #c3c3c5',

  radialBackground: '#d6d6d6',

  textColor: '#222',

  tooltipBackgroundColor: 'rgba(0, 0, 0, 0.7)',
  tooltipTextColor: '#fff',
};

const createTheme = (themeObj) => deepMerge({}, base, themeObj);

const dark = createTheme({
  axisTextColor: '#999',
  axisLineColor: '#717171',

  gridLineColor: '#555',

  legendColor: '#999',
  legendColorDisabled: '#717171',
  legendColorHover: '#c3c3c5',

  loadingInverted: false,

  numberTextColor: '#d6d6d6',
  numberBackgroundColor: '#444',
  numberInvertTextColor: '#fff',
  numberInvertPositiveBackgroundColor: 'rgb(28, 125, 68)',
  numberInvertNegativeBackgroundColor: 'rgb(28, 125, 68)',
  numberPositiveColor: '#2cc36b',
  numberNegativeColor: '#d14233',
  numberNeutralColor: '#444',

  panelBackgroundColor: '#444',
  panelBoxShadow: '0px 2px 2px #222',

  radialBackground: '#616161',

  textColor: '#d6d6d6',

  tooltipBackgroundColor: 'rgba(0, 0, 0, 0.7)',
  tooltipTextColor: '#fff',
});

const createRangeTheme = (name, type, min, max, mid) =>
  createObjectWithKey(name, { min, max, mid, name, type });

const rTypes = {
  tri: 'Tricolored',
  diverge: 'Diverging',
  mono: 'Monochromatic',
};

const lighten = (color) => tinycolor(color).lighten(40);

/**
 * Combines two colors.
 * @param { String } color1: First color to mix.
 * @param { String } color2: Second color to mix.
 * @param { Number } percentage: The percentage to mix.
 * @returns { Object } The mixed color.
 */
export const mixColors = (color1, color2, percentage = 50) =>
  tinycolor.mix(color1, color2, percentage);

const presetRed = '#eb144c';
const presetOrange = '#ff6900';
const presetYellow = '#fcb900';
const presetGreen = '#00d034';
const presetLightBlue = '#0693e3';
const presetBlue = '#2628f0';
const presetPurple = '#9900ef';
const presetBrown = '#b57133';
const presetGray = '#abb8c3';
const presetTeal = '#0dd7ad';
const presetBlack = '#000000';

export const numberRangeThemes = {
  ...createRangeTheme('default', rTypes.tri, presetRed, presetGreen, presetGray),
  ...createRangeTheme('american', rTypes.tri, presetRed, presetBlue, presetGray),
  ...createRangeTheme('traffic', rTypes.tri, presetRed, presetGreen, presetYellow),
  ...createRangeTheme('primary', rTypes.tri, presetRed, presetLightBlue, presetYellow),
  ...createRangeTheme('redToGreen', rTypes.diverge, presetRed, presetGreen),
  ...createRangeTheme('redToYellow', rTypes.diverge, presetRed, presetYellow),
  ...createRangeTheme('blueToOrange', rTypes.diverge, presetBlue, presetOrange),
  ...createRangeTheme('redToBlack', rTypes.diverge, presetRed, presetBlack),
  ...createRangeTheme('redToLightBlue', rTypes.diverge, presetRed, presetLightBlue),
  ...createRangeTheme('purpleToGreen', rTypes.diverge, presetPurple, presetGreen),
  ...createRangeTheme('purpleToYellow', rTypes.diverge, presetPurple, presetYellow),
  ...createRangeTheme('brownToTeal', rTypes.diverge, presetBrown, presetTeal),
  ...createRangeTheme('redMono', rTypes.mono, presetRed, lighten(presetRed)),
  ...createRangeTheme('orangeMono', rTypes.mono, presetOrange, lighten(presetOrange)),
  ...createRangeTheme('yellowMono', rTypes.mono, presetYellow, lighten(presetYellow)),
  ...createRangeTheme('greenMono', rTypes.mono, presetGreen, lighten(presetGreen)),
  ...createRangeTheme('lBlueMono', rTypes.mono, presetLightBlue, lighten(presetLightBlue)),
  ...createRangeTheme('blueMono', rTypes.mono, presetBlue, lighten(presetBlue)),
  ...createRangeTheme('purpleMono', rTypes.mono, presetPurple, lighten(presetPurple)),
  ...createRangeTheme('brownMono', rTypes.mono, presetBrown, lighten(presetBrown)),
};

/**
 * Gets the theme definition from the given theme name.
 * @param { String } themeName: The name of the theme.
 * @returns { Object } The theme definition.
 */
export const getNumberRangeTheme = (themeName) =>
  numberRangeThemes[themeName] || numberRangeThemes.default;

/**
 * Determines the color of a given value in a range of a given theme.
 * @param { Number } value: The value to determine the color.
 * @param { Number } minValue: The min value.
 * @param { Number } maxValue: The max value.
 * @param { String } themeName: The name of the theme.
 * @param { Boolean } reverse: If the theme colors are reversed.
 * @returns { Object } The color of the value.
 */
export const determineRangeValueColor = (value, minValue, maxValue, themeName, reverse) => {
  const { min, max, mid } = getNumberRangeTheme(themeName);
  let minColor = reverse ? max : min;
  let maxColor = reverse ? min : max;

  const range = maxValue - minValue;
  const startValue = value - minValue;
  const decimal = startValue / range;
  let percentage = decimal * 100;

  if (mid) {
    if (percentage > 50) {
      percentage -= 50;
      percentage *= 2;
      minColor = mid;
    } else {
      percentage *= 2;
      maxColor = mid;
    }
  }
  if (percentage < 0) {
    percentage = 0;
  }
  if (percentage > 100) {
    percentage = 100;
  }
  return mixColors(minColor, maxColor, percentage);
};

/**
 * Determines the 5 palette theme colors.
 * @param { String } min: The min color.
 * @param { String } max: The max color.
 * @param { String } mid: The mid color.
 * @param { Boolean } reverse: If the theme colors are reversed.
 * @returns { Object } The 5 palette colors.
 */
export const determineRangeColors = (min, max, mid, reverse) => {
  const minColor = reverse ? max : min;
  const maxColor = reverse ? min : max;
  const midColor = mid || mixColors(minColor, maxColor);
  const midMinColor = mixColors(minColor, midColor);
  const midMaxColor = mixColors(maxColor, midColor);
  return { minColor, midMinColor, midColor, midMaxColor, maxColor };
};

const createJsonTreeTheme = (
  background = '#fff',
  text = '#555',
  key = '#2e8ece',
  value = '#2cc36b',
  number = 'rgb(191, 114, 47)',
  empty = 'rgb(134, 42, 33)',
) => ({
  base00: background, // Background Color
  base01: text, // Key Modal Label Color
  base02: text, // Left indent border, Object Background.
  base03: value, // Unused
  base04: text, // Item Count String, Add key modal border
  base05: empty, // Undefined, Add key modal background
  base06: text, // Unused
  base07: key, // Brace Color, Key Color
  base08: empty, // NaN Color
  base09: value, // Ellipsis, Strings, editVariable cancel/remove, Validation failure background.
  base0A: empty, // Null Color, RegEx
  base0B: number, // Float
  base0C: text, // Array Key
  base0D: key, // Copy To Clipboard
  base0E: number, // Boolean, Collapsed Icon, Edit Variable edit/add/check
  base0F: number, // Number
});

export const jsonTreeThemes = {
  light: createJsonTreeTheme(),
  dark: createJsonTreeTheme(
    '#222',
    '#fff',
    '#2e8ece',
    'rgb(38, 116, 169)',
    '#e98b39',
    'rgb(171, 54, 42)',
  ),
};

export const themes = {
  default: base,
  dark,
};

/**
 * getTheme
 * @param { String } theme: The theme name.
 * @return { Object } The theme definition
 */
export const getTheme = (theme) => themes[theme] || themes[DEFAULT];

export default themes;
